<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import WelfareDefault from './components/Welfare.default.vue'
import { useCoinDisplayHold } from './composables/useCoinDisplayHold'
import { api } from '@/api'
import { useLoadingStore, useRewardsRedDotStore, useToastStore, useUserStore } from '@/stores'
import { isAppInstalled, isChrome, isEdge, isInThirdAppBrowser, isSafari, isTiktok } from '@/utils/bowserEnv'
import { isAndroid, isIOS } from '@/constants'
import { TASK_TYPE } from '@/utils/enum'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { pageViewTrack } from '@/composables/tracks'
import { resources, theme } from '@/config/brands'
import { t } from '@/i18n/utils'

const config = {
  theme: {
    btnBg: theme.bgTag,
    btnText: theme.primaryColor,
    coinsColor: theme.primaryColor,
  },
  resources: {
    homeBg: '',
    welfareGold: resources.coin,
    welfareBox: '/images/vowpath/welfare/gift.png',
  },
  texts: {
    get myWallet() { return t('MyWallet') },
    get coins() { return t('coins') },
    get dailyCheckIn() { return t('DailyCheckIn') },
    get youHaveCheckedIn() { return t('youHaveCheckedIn') },
    get dayStraight() { return t('dayStraight') },
    get checkIn() { return t('CheckInButton') },
    get tomorrowToGetMore() { return t('GetTomorrow') },
    get tomorrowCheckIn() { return t('tomorrowCheckIn') },
    get tasks() { return t('Tasks') },
    get checkInGet() { return t('checkInGet') },
    get get() { return t('GetCoins1') },
    get ok() { return t('OK') },
    get go() { return t('GO') },
    get done() { return t('TaskDone') },
    day(day: number) { return t('day', { day }) },
    get guideAddStep1() { return t('Click') },
    get guideAddAtTopRight() { return t('guideAddAtTopRight') },
    get guideAddAtBottom() { return t('guideAddAtBottom') },
    get guideAddAtBottomRight() { return t('AtBottomRight') },
    get guideAddShare() { return t('guideAddShare') },
    get guideAddToScreen() { return t('guideAddToScreen') },
    get guideOpenTitle() { return t('MenuExplain') },
    get guideOpenAction() { return t('OpenInExternalBrowser') },
  },
}

const { $toast } = useToastStore()
const { $showLoading, $hideLoading } = useLoadingStore()
const { elementClick } = useElementClick()
const userStore = useUserStore()
const route = useRoute()

// 定义背景图 URL 供 CSS v-bind 使用
const homeBgUrl = config.resources.homeBg ? `url(${config.resources.homeBg})` : 'none'

const signed = ref(false)
const progress = ref<any>([])
const modalSigned = ref(false)
const finishPopup = ref(false)
const claimingTask = ref(false)
const progressInfo = ref<any>()
const tomorrowInfo = ref<any>()
const checkInDayNum = ref(0)
const showTabbar = computed(() => route.query.from !== 'reward')

async function fetchProgress() {
  try {
    const { data } = await api.get<any>('/member/task/signTaskInfo')
    signed.value = data.isSign
    progress.value = data.memberSignResponseList

    // 当前签到信息
    if (signed.value) {
      for (let i = progress.value.length - 1; i >= 0; i--) {
        if (progress.value[i].isSign) {
          progressInfo.value = progress.value[i]
          tomorrowInfo.value = progress.value[i + 1] ?? null
          break
        }
      }
    }
    else {
      for (let i = 0; i < progress.value.length; i++) {
        if (!progress.value[i].isSign) {
          progressInfo.value = progress.value[i]
          tomorrowInfo.value = progress.value[i + 1] ?? null
          break
        }
      }
    }

    // 领取天数
    for (let i = progress.value.length - 1; i >= 0; i--) {
      if (progress.value[i].isSign) {
        checkInDayNum.value = i + 1
        break
      }
    }
    await userStore.init({ force: true })
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg || 'get check in progress failed')
  }
}

const { hold: holdCoinDisplay, release: releaseCoinDisplay } = useCoinDisplayHold()
// 离开页面时兜底解除冻结，保证顶部余额显示补齐
onBeforeUnmount(releaseCoinDisplay)

async function onCheckIn() {
  if (signed.value || claimingTask.value || finishPopup.value || modalSigned.value)
    return

  try {
    $showLoading()
    // 签到后 store 余额会立刻刷新，先冻结顶部显示，等收取动画结束再更新
    holdCoinDisplay()
    await api.get<any>('/member/task/sign')
    modalSigned.value = true
    pageViewTrack('check_in')
    fetchProgress()
    // 签到成功后重新聚合 Rewards Tab 红点
    useRewardsRedDotStore().refresh({ force: true })
  }
  catch (error: any) {
    releaseCoinDisplay()
    console.error(error)
    $toast(error.msg || 'check in failed')
  }
  finally {
    $hideLoading()
    elementClick('check_in')
  }
}

// #region tasks
type ITask = TTaskList & {
  type: string
  status: string
}
const tasks = ref<ITask[]>([])
const showGuidePopupAddHome = ref(false)
const showGuideOpen = ref(false)

const coins = ref(0)

const showAddTasks = computed(() => {
  const ios = isIOS && (isChrome() || isSafari() || isEdge())
  const android = isAndroid && isChrome()
  return (ios || android) && !isTiktok()
})

async function fatchTasks() {
  try {
    const { data } = await api.post<null, PostTaskListResponse, PostTaskListBody>('/task/list', {})

    // 任务列表
    tasks.value = (data || []).filter(item => (
      item.taskType === 0
      || item.taskType === 1
      || (item.taskType === 2 && showAddTasks.value)
    )) as ITask[]
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg)
  }
}

const getBtnStatus = function (item: ITask) {
  if (item.taskType === TASK_TYPE.BIND || item.taskType === TASK_TYPE.ADD_BROWSER) {
    if (item.buttonState === 2)
      return config.texts.ok
    else
      return config.texts.go
  }

  if (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN) {
    if (isAppInstalled())
      return config.texts.ok
    else
      return config.texts.go
  }
  return ''
}

function finishTaskTrack(taskType: number) {
  if (taskType === TASK_TYPE.BIND)
    pageViewTrack('task_success_login')

  else if (taskType === TASK_TYPE.ADD_BROWSER)
    pageViewTrack('task_success_browser')

  else if (taskType === TASK_TYPE.ADD_TO_HOME_SCREEN)
    pageViewTrack('task_success_homescreen')
}

async function getCoins(item: ITask) {
  if (claimingTask.value || finishPopup.value || modalSigned.value || item.buttonState === 1)
    return

  claimingTask.value = true
  holdCoinDisplay()
  try {
    const { code } = await api.post<null, PostTaskCoinsResponse, PostTaskCoinsBody>('/task/coins', {
      taskConfigDetailId: item.id,
      taskType: item.taskType,
      coins: item.coins,
    })
    if (code === 0) {
      finishTaskTrack(item.taskType)
      item.buttonState = 1
      finishPopup.value = true
      coins.value = item.coins
      fatchTasks()
      userStore.init({ force: true })
    }
    else {
      releaseCoinDisplay()
    }
  }
  catch (error: any) {
    console.error(error)
    releaseCoinDisplay()
    $toast(error.msg)
  }
  finally {
    claimingTask.value = false
  }
}
function handleTask(item: ITask) {
  if (item.buttonState === 1 || claimingTask.value || finishPopup.value || modalSigned.value)
    return

  elementClick(getBtnStatus(item).toUpperCase())

  // 领取
  if (item.buttonState === 2 || (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN && isAppInstalled())) {
    getCoins(item)
    return
  }

  if (item.taskType === TASK_TYPE.BIND) {
    tripartiteLoginPage('Rewards')
    return
  }

  if (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN) {
    showGuidePopupAddHome.value = true
    pageViewTrack('task_go_homescreen')
  }

  if (item.taskType === TASK_TYPE.ADD_BROWSER) {
    showGuideOpen.value = true
    pageViewTrack('task_go_browser')
  }
}

function onCheckInOk() {
  modalSigned.value = false
  if (isInThirdAppBrowser())
    showGuideOpen.value = true
}
// #endregion

onMounted(async () => {
  fetchProgress()
  fatchTasks()
})
</script>

<template>
  <WelfareDefault
    :config="config"
    :home-bg-url="homeBgUrl"
    :signed="signed"
    :progress="progress"
    :modal-signed="modalSigned"
    :progress-info="progressInfo"
    :tomorrow-info="tomorrowInfo"
    :check-in-day-num="checkInDayNum"
    :show-tabbar="showTabbar"
    :tasks="tasks"
    :show-guide-popup-add-home="showGuidePopupAddHome"
    :show-guide-open="showGuideOpen"
    :finish-popup="finishPopup"
    :coins="coins"
    :claiming-task="claimingTask"
    :user-coin-balance="userStore.$state.coinBalance"
    :get-btn-status="getBtnStatus"
    :on-check-in="onCheckIn"
    :handle-task="handleTask"
    :on-check-in-ok="onCheckInOk"
    @update:modal-signed="modalSigned = $event"
    @update:show-guide-popup-add-home="showGuidePopupAddHome = $event"
    @update:show-guide-open="showGuideOpen = $event"
    @update:finish-popup="finishPopup = $event"
  />
</template>
